<template>
  <b-modal
    :title="title"
    v-model="showModal"
    :ok-disabled="!formState"
    @ok.prevent="submit"
    @cancel.prevent="cancel"
  >
    <b-form @submit.prevent>
      <b-form-group
        label="Name"
        label-for="meeting-name"
        invalid-feedback="Please enter a name">
        <b-form-input
          id="meeting-name"
          v-model.trim="form.name"
          required
          maxlength="100"
          :state="errors.length == 0 ? null : form.name ? null : false">
        </b-form-input>
      </b-form-group>
      <b-row>
        <b-col>
          <b-form-group
            label="Type"
            label-for="meeting-type">
            <b-form-select
              id="meeting-type"
              v-model="form.type"
            >
              <option v-if="!isKickoffCreated" value="Kick-Off Meeting">Kick-Off Meeting</option>
              <option value="Weekly Project Meeting">Weekly Project Meeting</option>
              <option value="Partnering Meeting">Partnering Meeting</option>
              <option value="Executive Session">Executive Session</option>
              <option value="Closeout Meeting">Closeout Meeting</option>
              <option value="Other">Other</option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Date"
            label-for="meeting-date"
            invalid-feedback="Please enter a date">
            <b-form-datepicker
              id="meeting-date"
              v-model="form.date"
              required
              :state="errors.length == 0 ? null : form.date ? null : false"
              size="sm"
              placeholder="mm/dd/yyyy"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            ></b-form-datepicker>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            label="Start Time"
            label-for="meeting-start"
            invalid-feedback="Please enter a start time">
            <b-form-input
              id="meeting-start"
              v-model="form.start"
              type="time"
              required
              :state="errors.length == 0 ? null : form.start ? null : false">
            </b-form-input>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="End Time"
            label-for="meeting-end"
            invalid-feedback="Please enter an end time">
            <b-form-input
              id="meeting-end"
              v-model="form.end"
              type="time"
              required
              :state="errors.length == 0 ? null : form.end ? null : false">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="false">
        <b-col>
          <b-form-group 
            label="Virtual Meeting"
            label-for="meeting-online">
            <b-form-select 
              id="meeting-online"
              v-model="form.online">
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <h6>Invite people</h6>
      <b-row>
        <b-col>
          <SelectUsersList
            :users="modifiedUsers"
          ></SelectUsersList>
        </b-col>
      </b-row>
    </b-form>
    <BaseMessageBlock :messages="errors"></BaseMessageBlock>
    <template v-slot:modal-ok >
      <div style="width: 49px;" >
        <b-spinner v-if="sending" small label="Spinning"/>
        <div v-else>Add</div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";
import SelectUsersList from "@/sub-components/SelectUsersList.vue";

export default {
  props: {
    title: { type: String, default: 'Add Meeting'},
    meeting: { type: Object, default: null },
    users: { type: Array, default: () => [] },
    isKickoffCreated: { type: Boolean, default: false },
    projectId: String
  },
  components: {
    SelectUsersList
  },
  data() {
    return {
      errors: [],
      showModal: false,
      sending: false,
      form: this.newForm()
    }
  },
  methods: {
    show(){
      this.reset();
      this.showModal = true;
    },
    hide() {
      this.showModal = false;
    },
    submit() {
      this.addMeeting(this.form);
    },
    cancel() {
      this.reset();
      this.hide();
    },
    reset() {
      this.errors = [];
      this.form = this.newForm();
    },
    newForm() {
      return {
        date: "",
        start: "",
        end: "",
        name: "",
        type: this.defaultMeetingType,
        online: "No"
      };
    },
    validateMeeting(form) {
      const initErrors = this.errors.length;
      let now = new Date();
      now.setHours(0,0,0,0);
      if (!form.name) {
        this.errors.push({ message: "Title cannot be empty" });
      } 
      
      if (!form.date) {
        this.errors.push({ message: "Date cannot be empty "});
      } else if (now > new Date(form.date.replace(/-/g, '/'))) {
        this.errors.push({ message: "Date must be in the future" });
      } 

      if (!form.start) {
        this.errors.push({ message: "Start time cannot be empty" });
      }

      if (!form.end) {
        this.errors.push({ message: "End time cannot be empty" });
      }

      if (initErrors === this.errors.length) {
        //Date, start are present.
        let start = new Date(form.date.replace(/-/g, '/') + " " + form.start);
        let end = new Date(form.date.replace(/-/g, '/') + " " + form.end);
        
        if (start >= end) {
          this.errors.push({ message: "Start time must be before End time "});
        }
      } 
      
      return initErrors === this.errors.length;
    },
    addMeeting(form){
      this.errors = [];

      if (!this.validateMeeting(form)){
        return;
      }

      let start = this.$helpers.dateToAPIString(new Date(form.date.replace(/-/g, '/') + " " + form.start));
      let end = this.$helpers.dateToAPIString(new Date(form.date.replace(/-/g, '/') + " " + form.end));

      let request = {
        meetingId: this.$helpers.generateUUID(),
        projectId: this.projectId,
        name: form.name,
        type: form.type,
        start: start,
        end: end,
        online: form.online === "Yes",
        invitees: this.modifiedUsers.filter(u => u.selected).map(u => u.userId)
      };

      this.sending = true;
      axios
        .post(`${this.$api_url}/meeting/schedule`, request, this.getReqConfig())
        .then(() => {
          let newMeeting = {
            ...request,
            date: this.$helpers.dateToAPIString(new Date(form.date.replace(/-/g, '/'))),
            status: "Open",
            addedMetricCount: 0,
            closedMetricCount: 0,
            attendeeCount: 0
          };
          this.$emit('submit', newMeeting);
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.sending = false;
        });
    }
  },
  computed: {
    formState() {
      return this.form.name && this.form.date && this.form.start && this.form.end;
    },
    modifiedUsers() {
      return this.users.map(u => ({
        ...u,
        selected: true
      }))
    },
    defaultMeetingType() {
      return !this.isKickoffCreated ?
        "Kick-Off Meeting" :
        "Weekly Project Meeting";
    }
  }
}
</script>
<style scoped>
.border-owner {
  border-left-width: 6px;
  border-left-color: #4a708b;
}
.border-contractor {
  border-left-width: 6px;
  border-left-color: #34bf1b;
}
</style>